import type { Partner } from "@expert/shared-types";

import { addMessage, setupOrchestratorTimeout } from "@asurion-core/expert-workspace-timeline";
import { gaiaWsEventBus } from "@expert/gaia";
import { getLogger } from "@expert/logging";
import { Button, Container, Stack, Text } from "@mantine/core";

import { useExpertAssistStore } from "../../state";
import { lobs, partners } from "./configValues";
import { RadioGroup } from "./RadioGroup";

const logger = getLogger({ module: "DevToolsMenuItem" });

export function DevToolsMenuItem() {
    const { callSid, lob, partner, sessionId, setSessionDetails } = useExpertAssistStore();

    const isDisabled = !!callSid;

    const resetSession = ({ newPartner, newLob }: { newPartner: Partner; newLob?: string }) => {
        const newSessionId = crypto.randomUUID();
        setSessionDetails({ sessionId: newSessionId, callSid, partner: newPartner ?? partner, lob: newLob ?? lob });
    };

    const simulateSilentTimeout = () => {
        const correlationId = `dev-tool-timeout-${crypto.randomUUID()}`;
        const messageId = `dev-timeout-${crypto.randomUUID()}`;
        const messageText = "[Dev Tool] Simulated expert message (no GAIA response will arrive)";
        addMessage(sessionId, {
            id: messageId,
            text: messageText,
            type: "Expert",
            isUnread: false,
            status: "active",
        });
        setupOrchestratorTimeout(correlationId, sessionId, logger, messageId, {
            text: messageText,
            type: "Expert",
            source: "DevTool",
        });
    };

    const simulateOrchestratorError = () => {
        const timestamp = Date.now();
        const messageId = `dev-${timestamp}`;
        const messageText = "Customer is having issues with their device not turning on";
        addMessage(sessionId, {
            id: messageId,
            text: messageText,
            type: "Expert",
            isUnread: false,
            status: "active",
        });
        setTimeout(() => {
            gaiaWsEventBus.emit("gaia_ws_orchestrator-invoke-error", {
                name: "orchestrator-invoke-error",
                correlationId: `dev-tool-${timestamp}`,
                timestamp,
                data: undefined,
                body: {
                    message: "Failed to invoke orchestrator",
                    status: 500,
                    statusText: "Internal Server Error",
                    trackingId: `dev-tool-tracking-${timestamp}`,
                    responseText: "Internal server error",
                    body: JSON.stringify({
                        sessionId,
                        sessionGroupId: sessionId,
                        message: messageText,
                        userId: "dev-tool",
                        messageId,
                        messageType: "Search",
                        userType: "expert",
                    }),
                },
            });
        }, 1500);
    };

    return (
        <Container pb="sm">
            <RadioGroup
                label="Line of business"
                options={lobs}
                isDisabled={isDisabled}
                currentSelectedOption={lob}
                onChange={(newLob) => {
                    resetSession({ newPartner: "mcafee", newLob });
                }}
            />
            <RadioGroup
                label="Partner"
                options={partners}
                isDisabled={isDisabled}
                currentSelectedOption={partner}
                onChange={(newPartner) => {
                    resetSession({ newPartner });
                }}
            />

            <Text size="sm" fw={500} mt="md" mb="xs">
                GAIA Error Simulation
            </Text>
            <Stack gap="xs">
                <Button
                    size="xs"
                    variant="light"
                    color="red"
                    fz="xs"
                    onClick={simulateSilentTimeout}
                    disabled={!sessionId}
                >
                    Simulate Silent Timeout
                </Button>
                <Button
                    size="xs"
                    variant="light"
                    color="orange"
                    fz="xs"
                    onClick={simulateOrchestratorError}
                    disabled={!sessionId}
                >
                    Simulate Orchestrator Error (500)
                </Button>
            </Stack>
        </Container>
    );
}
